/**
 * Hooks para el feed "What's new" de la plataforma.
 *   Feed público: useUpdatesFeed()
 *   Admin: useAdminUpdates, useCreateUpdate, useUpdateUpdate, useDeleteUpdate,
 *          useUploadUpdateImage
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Types ───────────────────────────────────────────────────────────────────

export type UpdateCategory = 'feature' | 'improvement' | 'fix' | 'announcement' | 'event'
export type UpdateScopeCode = 'GLOBAL' | 'CL' | 'PE' | 'AR' | 'MX' | 'EC'

export interface PlatformUpdate {
  id: string
  title: string
  body_markdown: string
  image_url: string
  video_embed_url: string
  category: UpdateCategory
  category_display: string
  scope: UpdateScopeCode[]
  published_at: string
  action_url: string
  action_label: string
  // Solo admin:
  is_active?: boolean
  created_by_name?: string
  created_at?: string
  updated_at?: string
}

export interface FeedResponse {
  updates: PlatformUpdate[]
  count: number
}

export interface CreateUpdatePayload {
  title: string
  body_markdown?: string
  image_url?: string
  video_embed_url?: string
  category: UpdateCategory
  scope: UpdateScopeCode[]
  published_at: string
  is_active?: boolean
  action_url?: string
  action_label?: string
}

// ─── Query keys ──────────────────────────────────────────────────────────────

const keys = {
  feed: ['platform-updates', 'feed'] as const,
  adminList: ['platform-updates', 'admin', 'list'] as const,
}

// ─── Public feed ─────────────────────────────────────────────────────────────

export function useUpdatesFeed(enabled = true) {
  return useQuery({
    queryKey: keys.feed,
    queryFn: async () => {
      const res = await apiClient.get('/platform/updates/feed/', { params: { limit: 20 } })
      return res.data as FeedResponse
    },
    enabled,
    staleTime: 60 * 1000,
  })
}

/**
 * La novedad más reciente, para el punto del ícono 🎁. Se pide al cargar la app
 * (una sola tarjeta) y se refresca cada 30 minutos: el feed completo se pide
 * recién al abrir el cajón.
 */
export function useLatestUpdate() {
  return useQuery({
    queryKey: ['platform-updates', 'latest'],
    queryFn: async () => {
      const res = await apiClient.get('/platform/updates/feed/', { params: { limit: 1 } })
      return ((res.data as FeedResponse).updates[0] ?? null) as PlatformUpdate | null
    },
    staleTime: 30 * 60 * 1000,
  })
}

// ─── Admin CRUD ──────────────────────────────────────────────────────────────

export function useAdminUpdates() {
  return useQuery({
    queryKey: keys.adminList,
    queryFn: async () => {
      const res = await apiClient.get('/platform/updates/')
      return res.data as { updates: PlatformUpdate[]; count: number }
    },
  })
}

export function useCreateUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateUpdatePayload) => {
      const res = await apiClient.post('/platform/updates/', payload)
      return res.data as PlatformUpdate
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.adminList })
      qc.invalidateQueries({ queryKey: keys.feed })
    },
  })
}

export function useUpdateUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<CreateUpdatePayload>) => {
      const res = await apiClient.patch(`/platform/updates/${id}/`, payload)
      return res.data as PlatformUpdate
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.adminList })
      qc.invalidateQueries({ queryKey: keys.feed })
    },
  })
}

export function useDeleteUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/platform/updates/${id}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.adminList })
      qc.invalidateQueries({ queryKey: keys.feed })
    },
  })
}

export function useUploadUpdateImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const res = await apiClient.post('/platform/updates/upload-image/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data as { url: string; key: string }
    },
  })
}

// ─── Helpers UI ──────────────────────────────────────────────────────────────

export const CATEGORY_STYLES: Record<UpdateCategory, { label: string; cls: string }> = {
  feature: {
    label: 'Nueva',
    cls: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  },
  improvement: {
    label: 'Mejora',
    cls: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  },
  fix: {
    label: 'Corrección',
    cls: 'bg-warning-surface text-warning-content border-warning-border',
  },
  announcement: {
    label: 'Anuncio',
    cls: 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800',
  },
  event: {
    label: 'Evento',
    cls: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
  },
}

export const SCOPE_FLAGS: Record<UpdateScopeCode, { label: string; flag: string }> = {
  GLOBAL: { label: 'Global', flag: '🌎' },
  CL: { label: 'Chile', flag: '🇨🇱' },
  PE: { label: 'Perú', flag: '🇵🇪' },
  AR: { label: 'Argentina', flag: '🇦🇷' },
  MX: { label: 'México', flag: '🇲🇽' },
  EC: { label: 'Ecuador', flag: '🇪🇨' },
}
